import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { getOnEnter, RD_COMPONENTS, RdComponentType } from '../../_utils';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { InputBase, InputBaseComponentProps, InputBaseProps } from '../input-base';
import styles from './input-text.module.scss';

export type InputTextType = 'text' | 'password' | 'email';
export interface InputTextComponentProps
  extends Omit<InputBaseComponentProps, 'onChange' | 'suffix' | 'prefix' | 'type'> {
  /**
   * Aria label attribute for the input
   */
  ariaLabel?: string;

  /**
   * Input value
   */
  value?: string;

  /**
   * input type
   * @default text
   */
  type?: InputTextType;

  /**
   * ID of the prefix icon
   */
  prefixIcon?: IconID;

  /**
   * className of the prefix icon
   */
  prefixIconClassName?: string;

  /**
   * Flag for visibily of suffix icon only when input is in active state
   * ActiveState: either input is non-empty or in focus
   */
  showSuffixIconOnActiveInput?: boolean;

  /**
   * The maximum number of characters the user can enter into an input
   */
  maxLength?: number;

  /**
   * The callback function that is triggered when suffix icon is clicked
   */
  onSuffixIconClick?: (e: React.MouseEvent) => void;

  /**
   * ID of the suffix icon
   */
  suffixIcon?: IconID;

  /**
   * size of the suffix icon
   */
  suffixIconSize?: IconSize;

  /**
   * color of the suffix icon
   */
  suffixIconColor?: IconColor;

  /**
   * className of the suffix icon
   */
  suffixIconClassName?: string;

  /**
   * The callback function that is triggered when prefix icon is clicked
   */
  onPrefixIconClick?: (e: React.MouseEvent) => void;

  /**
   * The callback function that is triggered when user  input
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;

  /**
   * Specifies the format of the value presented
   */
  formatter?: (value: string) => string;

  /**
   * Specifies the value extracted from formatter
   */
  parser?: (value: string) => string;

  /**
   * Aria label attribute for the prefix
   */
  ariaLabelPrefix?: string;

  /**
   * To hide the prefix icon from screen reader
   */
  ariaPrefixHidden?: boolean;

  /**
   * Aria label attribute for the suffix
   */
  ariaLabelSuffix?: string;

  /**
   * To hide the suffix icon from screen reader
   */
  ariaSuffixHidden?: boolean;

  /**
   * Whether to put the flex grow property on the component container.
   */
  grow?: boolean;
  /**
   * base Input className
   */
  inputClassName?: string;
  /**
   * Input ref
   */
  inputRef?: React.RefObject<HTMLInputElement>;
}

export type InputTextProps = InputTextComponentProps & Omit<InputBaseProps, 'onChange'>;

export const InputText: RdComponentType<InputTextProps> = ({
  ariaLabel,
  ariaLabelPrefix = '',
  ariaLabelSuffix = '',
  ariaPrefixHidden = false,
  ariaSuffixHidden = false,
  className,
  name,
  id,
  prefixIcon,
  suffixIcon,
  value = '',
  maxLength,
  onSuffixIconClick,
  onPrefixIconClick,
  onChange,
  formatter,
  parser,
  type = 'text',
  hasError = false,
  helperText,
  grow,
  suffix,
  prefix,
  suffixIconSize = IconSize.MEDIUM,
  suffixIconColor = IconColor.GRAY,
  suffixIconClassName,
  prefixIconClassName,
  inputClassName,
  inputRef,
  showSuffixIconOnActiveInput = false,
  onFocus,
  onBlur,
  ...restProps
}: InputTextProps) => {
  const getFormattedValue = useCallback(
    (val: string) => {
      if (formatter) {
        return formatter(val);
      }
      return val;
    },
    [formatter]
  );
  const inputId = id || (name ? `input-${name}` : undefined);
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState<string>(
    getFormattedValue(value.slice(0, maxLength))
  );

  useEffect(() => {
    setInputValue(getFormattedValue(value.slice(0, maxLength)));
  }, [value, getFormattedValue, maxLength]);

  const handleSuffixIconClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (onSuffixIconClick) {
      onSuffixIconClick(event as React.MouseEvent);
    }
  };
  const handlePrefixClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (onPrefixIconClick) {
      onPrefixIconClick(event as React.MouseEvent);
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let val: string = event.target.value;
    if (parser) {
      val = parser(val);
    }
    if (onChange) {
      onChange(event, val);
    }
    const formattedValue = getFormattedValue(val);
    setInputValue(formattedValue);
  };

  const isSuffixIconVisible = () => {
    if (suffixIcon && showSuffixIconOnActiveInput) {
      return hasFocus || !_.isEmpty(inputValue);
    }
    return !!suffixIcon;
  };

  const getSuffixComponent = () => {
    if (isSuffixIconVisible()) {
      return (
        <div
          onClick={handleSuffixIconClick}
          className={cx(suffixIconClassName, styles.iconStyle, styles.suffixIcon)}
          role="button"
          aria-label={ariaLabelSuffix}
          aria-hidden={ariaSuffixHidden}
          tabIndex={ariaSuffixHidden ? -1 : 0}
          onKeyDown={getOnEnter(handleSuffixIconClick)}
        >
          <Icon color={suffixIconColor} size={suffixIconSize} id={suffixIcon as IconID} />
        </div>
      );
    }
    return null;
  };
  const getPrefixComponent = () => {
    if (prefixIcon) {
      return (
        <div
          onClick={handlePrefixClick}
          className={cx(styles.iconStyle, styles.prefixIcon)}
          role="button"
          aria-label={ariaLabelPrefix}
          aria-hidden={ariaPrefixHidden}
          tabIndex={ariaPrefixHidden ? -1 : 0}
          onKeyDown={getOnEnter(handlePrefixClick)}
        >
          <Icon
            color={IconColor.GRAY}
            size={IconSize.MEDIUM}
            id={prefixIcon}
            className={prefixIconClassName}
          />
        </div>
      );
    }
    return null;
  };

  const containerClassNames = cx(className, styles.container, {
    [styles.grow]: grow,
  });

  return (
    <div data-radiant-v2 className={containerClassNames}>
      <InputBase
        type={type}
        name={name}
        onChange={handleChange}
        value={inputValue}
        aria-label={ariaLabel}
        id={inputId}
        suffix={getSuffixComponent()}
        prefix={getPrefixComponent()}
        maxLength={maxLength}
        hasError={hasError}
        helperText={helperText}
        className={inputClassName}
        inputRef={inputRef}
        onFocus={(e) => {
          setHasFocus(true);
          if (onFocus) onFocus(e);
        }}
        onBlur={(e) => {
          setHasFocus(false);
          if (onBlur) onBlur(e);
        }}
        {...restProps}
      />
      {maxLength && !helperText && (
        <span className={styles.inlineMsgStyle}>
          {inputValue.length}/{maxLength}
        </span>
      )}
    </div>
  );
};

InputText.RD_COMPONENT = RD_COMPONENTS.INPUT_NUMBER;
